import { IChannel, ISubChannelSummary } from "@Interfaces/Entities/Channels";
import {
    IPresentationEligibility,
    IRemoteState,
    PresentationIneligibleReason,
    RemoteAction,
} from "@Interfaces/Entities/Presentation";

/** Link the projector computer opens: display only, no login needed. */
export const buildScreenUrl = (code: string) =>
    `${window.location.origin}/screen/${code}`;

/** A link as people read it aloud: no "https://" or trailing slash. Copy keeps the full URL. */
export const formatLinkForDisplay = (url: string) =>
    url.replace(/^https?:\/\//, "").replace(/\/$/, "");

/**
 * Link the admin's phone opens to control the session (the phone remote). The PIN
 * sits in the fragment so it never reaches server logs.
 */
export const buildRemoteUrl = (code: string, pin: string | null) =>
    `${window.location.origin}/remote/${code}${pin ? `#pin=${pin}` : ""}`;

/** The event's normal voting link, the same one QR Export uses. */
export const buildVotingUrl = (linkId: string) =>
    `${window.location.origin}/share?ch=${linkId}`;

/** NPS score with an explicit sign and a true minus (U+2212): "+38", "0", "−12". */
export const formatSignedScore = (score: number) => {
    if (score > 0) return `+${score}`;
    if (score < 0) return `−${Math.abs(score)}`;
    return "0";
};

/** Whole number with the viewer's thousands separator: "1,284", "1.284". */
export const formatCount = (value: number, language?: string) => {
    try {
        return new Intl.NumberFormat(language).format(value);
    } catch {
        return new Intl.NumberFormat().format(value);
    }
};

/** Translation key that explains why Present Mode is unavailable. */
export const getIneligibleReasonKey = (reason: PresentationIneligibleReason) =>
    `present.reason.${reason}`;

const isLiveSubChannel = (subChannel: ISubChannelSummary) =>
    subChannel.active !== false && subChannel.closed !== true;

/**
 * First-pass eligibility from data the page already has, so the header button can be
 * disabled without a request. The server's answer in the dialog is authoritative.
 */
export const getClientPresentEligibility = (
    channel: IChannel
): IPresentationEligibility => {
    const subChannels = channel.sub_channels ?? [];
    const liveSubChannels = subChannels.filter(isLiveSubChannel);
    const disabled = (reason: PresentationIneligibleReason) => ({
        enabled: false,
        reason,
    });

    if (channel.closed) return disabled("closed");
    if (channel.active === false) return disabled("inactive");
    if (channel.settings?.allow_double_vote) return disabled("double_vote");
    if (liveSubChannels.length > 1) {
        return disabled("several_active_sub_events");
    }
    if (subChannels.length > 0 && liveSubChannels.length === 0) {
        return disabled("no_active_sub_event");
    }
    if (subChannels.length === 0 && (channel.polls?.length ?? 0) === 0) {
        return disabled("no_polls");
    }
    return { enabled: true, reason: null };
};

/** The PIN from a remote link's fragment (`#pin=…`), or null when it's missing. */
export const readRemotePin = (hash: string) => {
    const pin = new URLSearchParams(hash.replace(/^#/, "")).get("pin");
    return pin && /^[a-f0-9]{32}$/.test(pin) ? pin : null;
};

/** Pages in presenting order: the live count (null), then every visible poll. */
const pagesOf = (polls: IRemoteState["polls"]) => [
    null,
    ...polls.filter((poll) => !poll.hidden).map((poll) => poll.poll_id),
];

/**
 * What a tap does, applied on the phone straight away so it feels instant. Mirrors the
 * server's rules; the server's answer then replaces this guess.
 */
export const applyRemoteActionLocally = (
    state: IRemoteState,
    action: RemoteAction
): IRemoteState => {
    const pages = pagesOf(state.polls);
    const index = Math.max(0, pages.indexOf(state.current.poll_id));
    // Puts `pollId` on screen, recounting pages from `next.polls` (hiding changes them).
    const withCurrent = (
        next: IRemoteState,
        pollId: string | null
    ): IRemoteState => {
        const nextPages =
            next.polls === state.polls ? pages : pagesOf(next.polls);
        const at = Math.max(0, nextPages.indexOf(pollId));
        return {
            ...next,
            current: {
                poll_id: nextPages[at],
                position: at + 1,
                total: nextPages.length,
            },
        };
    };

    switch (action.type) {
        case "start":
            return state.status === "live"
                ? state
                : withCurrent({ ...state, status: "live" }, null);
        case "end":
            return withCurrent({ ...state, status: "ended" }, null);
        case "next":
            return withCurrent(
                state,
                pages[Math.min(index + 1, pages.length - 1)]
            );
        case "prev":
            return withCurrent(state, pages[Math.max(index - 1, 0)]);
        case "go":
            return withCurrent(state, action.poll_id);
        case "hide": {
            const shown = pages.slice(1) as string[];
            if (!shown.includes(action.poll_id) || shown.length <= 1)
                return state;
            const polls = state.polls.map((poll) =>
                poll.poll_id === action.poll_id
                    ? { ...poll, hidden: true }
                    : poll
            );
            let current = state.current.poll_id;
            if (current === action.poll_id) {
                // Next visible poll, else the previous one, else the live count.
                const at = shown.indexOf(action.poll_id);
                current = shown[at + 1] ?? shown[at - 1] ?? null;
            }
            return withCurrent({ ...state, polls }, current);
        }
        case "show": {
            const polls = state.polls.map((poll) =>
                poll.poll_id === action.poll_id
                    ? { ...poll, hidden: false }
                    : poll
            );
            return withCurrent({ ...state, polls }, state.current.poll_id);
        }
        default:
            return state;
    }
};

const MAX_RETRY_DELAY_MS = 8000;

/** Exponential backoff for the screen's and phone's polling: doubles per failure, up to 8 s. */
export const retryDelay = (failures: number, baseMs: number) =>
    Math.min(baseMs * 2 ** failures, MAX_RETRY_DELAY_MS);

/** A per-tab yes/no flag in sessionStorage; storage failures just mean "not set". */
const writeSessionFlag = (key: string, on: boolean) => {
    try {
        if (on) sessionStorage.setItem(key, "1");
        else sessionStorage.removeItem(key);
    } catch {
        // Per-tab convenience only.
    }
};

const readSessionFlag = (key: string) => {
    try {
        return sessionStorage.getItem(key) === "1";
    } catch {
        return false;
    }
};

const endedKey = (code: string) => `tb.present.ended.${code.toUpperCase()}`;

/**
 * Per tab: this phone ended the session for this link. Survives a reload (iOS reloads
 * background tabs), so the phone doesn't silently take the remote back.
 */
export const rememberRemoteEnded = (code: string, ended: boolean) =>
    writeSessionFlag(endedKey(code), ended);

export const wasRemoteEnded = (code: string) => readSessionFlag(endedKey(code));

const collapsedKey = (code: string) =>
    `tb.present.collapsed.${code.toUpperCase()}`;

/** Per tab: the presenter folded the phone's poll list away. */
export const rememberPollsCollapsed = (code: string, collapsed: boolean) =>
    writeSessionFlag(collapsedKey(code), collapsed);

export const werePollsCollapsed = (code: string) =>
    readSessionFlag(collapsedKey(code));
